/**
 * @param {string} s — `YYYY-MM-DD HH:mm:ss` или `YYYY-MM-DD HH:mm`
 * @returns {Date|null}
 */
function parseLocalDateTime (s) {
  if (!s || typeof s !== 'string') return null;
  const m = s.trim().match(/^(\d{4}-\d{2}-\d{2})(?:[ T](\d{2}):(\d{2})(?::(\d{2}))?)?$/);
  if (!m) return null;
  const [yy, mm, dd] = m[1].split('-').map(Number);
  const h = m[2] != null ? Number(m[2]) : 0;
  const min = m[3] != null ? Number(m[3]) : 0;
  const sec = m[4] != null ? Number(m[4]) : 0;
  return new Date(yy, mm - 1, dd, h, min, sec, 0);
}

function hasTimePart (s) {
  return typeof s === 'string' && /\d{2}:\d{2}/.test(s);
}

/**
 * @param {{ useSchedule: boolean, startDate: Date|string, endDate: Date|string }} schedule
 * @param {{ endOnly?: boolean }} [options] — только end_date (массовое LCD-изображение)
 * @returns {{ payload: { start_date?: string, end_date?: string }|null, error: string|null }}
 */
export function buildSchedulePayload (schedule, options = {}) {
  if (!schedule || !schedule.useSchedule) {
    return { payload: null, error: null };
  }

  const pad = (n) => String(n).padStart(2, '0');

  const formatYmdLocal = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;

  const toYmd = (v) => {
    if (!v) return '';
    if (v instanceof Date) return formatYmdLocal(v);
    if (typeof v === 'string') {
      const parsed = parseLocalDateTime(v);
      if (parsed) return formatYmdLocal(parsed);
      if (/^\d{4}-\d{2}-\d{2}$/.test(v)) return v;
    }
    const dt = new Date(v);
    if (Number.isNaN(dt.getTime())) return '';
    return formatYmdLocal(dt);
  };

  const formatYmdHis = (d) => {
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`;
  };

  const makeLocalDateTime = (ymd, h, m, s) => {
    const [yy, mm, dd] = ymd.split('-').map(Number);
    return new Date(yy, mm - 1, dd, h, m, s, 0);
  };

  const todayYmd = formatYmdLocal(new Date());

  if (options.endOnly) {
    const end = parseLocalDateTime(String(schedule.endDate || ''));
    if (!end || Number.isNaN(end.getTime())) {
      return { payload: null, error: 'Укажите дату и время сброса изображения' };
    }
    const endYmd = formatYmdLocal(end);
    if (endYmd < todayYmd) {
      return { payload: null, error: 'Дата сброса не может быть в прошлом' };
    }
    if (end.getTime() < Date.now()) {
      return { payload: null, error: 'Дата и время сброса не могут быть в прошлом' };
    }
    return {
      payload: {
        end_date: formatYmdHis(end),
      },
      error: null,
    };
  }

  if (!schedule.startDate || !schedule.endDate) {
    return { payload: null, error: 'Укажите дату и время начала и окончания периода' };
  }

  const startRaw = schedule.startDate instanceof Date
    ? formatYmdHis(schedule.startDate)
    : String(schedule.startDate);
  const endRaw = schedule.endDate instanceof Date
    ? formatYmdHis(schedule.endDate)
    : String(schedule.endDate);

  const startFromTime = hasTimePart(startRaw) ? parseLocalDateTime(startRaw) : null;
  const endFromTime = hasTimePart(endRaw) ? parseLocalDateTime(endRaw) : null;

  const startYmd = startFromTime ? formatYmdLocal(startFromTime) : toYmd(schedule.startDate);
  const endYmd = endFromTime ? formatYmdLocal(endFromTime) : toYmd(schedule.endDate);

  if (!startYmd || !endYmd) {
    return { payload: null, error: 'Некорректные даты' };
  }

  if (startYmd < todayYmd) {
    return { payload: null, error: 'Начало периода не может быть в прошлом' };
  }

  if (endYmd < startYmd) {
    return { payload: null, error: 'Окончание периода не может быть меньше начала' };
  }

  const nowRounded = new Date();
  nowRounded.setSeconds(0, 0);

  const start = startFromTime || (startYmd === todayYmd ? nowRounded : makeLocalDateTime(startYmd, 0, 0, 0));
  const end = endFromTime || makeLocalDateTime(endYmd, 23, 59, 59);

  if (start < nowRounded) {
    return { payload: null, error: 'Дата и время начала не могут быть в прошлом' };
  }

  if (start >= end) {
    return { payload: null, error: 'Дата и время начала должны быть раньше окончания' };
  }

  return {
    payload: {
      start_date: formatYmdHis(start),
      end_date: formatYmdHis(end),
    },
    error: null,
  };
}
